import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { Font } from 'three/examples/jsm/loaders/FontLoader';
import { LocationData, Rack, Server } from '../types';

interface Test3DViewProps {
  locations?: Record<string, LocationData>;
  currentLocation?: string;
}

const Test3DView: React.FC<Test3DViewProps> = ({ locations, currentLocation }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!mountRef.current) return;
    
    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      65, 
      mountRef.current.clientWidth / mountRef.current.clientHeight, 
      0.1, 
      1000
    );
    // Position camera to see the entire datacenter layout
    camera.position.z = 25;
    camera.position.y = 15;
    camera.position.x = 0;
    camera.lookAt(0, 0, 0);
    
    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    mountRef.current.appendChild(renderer.domElement);
    
    // Controls setup
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    
    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(0, 10, 10);
    scene.add(directionalLight);
    
    // Add a grid for reference
    const gridHelper = new THREE.GridHelper(100, 100, 0x404040, 0x404040);
    scene.add(gridHelper);
    
    // Add a floor
    const floorGeometry = new THREE.PlaneGeometry(60, 40);
    const floorMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x222222, 
      roughness: 0.8,
      metalness: 0.2 
    });
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = 0;
    scene.add(floor);
    
    // Add background gradient for atmosphere
    const bgGeometry = new THREE.SphereGeometry(80, 32, 32);
    const bgMaterial = new THREE.MeshBasicMaterial({
      color: 0x030312,
      side: THREE.BackSide
    });
    const background = new THREE.Mesh(bgGeometry, bgMaterial);
    scene.add(background);
    
    // Grid helper
    const gridHelper2 = new THREE.GridHelper(40, 40);
    scene.add(gridHelper2);
    
    // Create the datacenter layout with real data if available
    const locationData = locations && currentLocation ? locations[currentLocation] : null;
    createSampleDatacenter(scene, locationData);
    
    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();
    
    // Handle resize
    const handleResize = () => {
      if (!mountRef.current) return;
      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (mountRef.current && mountRef.current.contains(renderer.domElement)) {
        mountRef.current.removeChild(renderer.domElement);
      }
      // Dispose resources to prevent memory leaks
      scene.clear();
      renderer.dispose();
    };
  }, []);
  
  return (
    <div className="w-full h-screen flex flex-col">
      <div className="p-4 bg-gray-800 text-white">
        <h1 className="text-xl font-bold">3D Datacenter View Test</h1>
        <p className="text-sm">Rotate: Left-click and drag | Zoom: Scroll wheel | Pan: Right-click and drag</p>
      </div>
      <div ref={mountRef} className="flex-grow bg-gray-900" />
    </div>
  );
};

// Helper function to create datacenter matching the 2D layout (2 rows x 16 racks)
const createSampleDatacenter = (scene: THREE.Scene, locationData?: LocationData | null) => {
  // If no location data is provided, still continue but use sample data
  if (!locationData) {
    console.log('No location data provided, using sample data');
    // We'll still use the default visualization with random data
  }
  // Spacing and layout variables
  const rackWidth = 1.2;
  const rackDepth = 1;
  const rackHeight = 2;
  const rackSpacing = 1.4;
  const rowSpacing = 5; // Spacing between rows (for the cold aisle)
  
  // Create the hot and cold aisle indicators (centered cold aisle)
  // Note: Cold aisle is centered at z=0, with hot aisles on both sides
  createAisleIndicator(scene, 0, 0, -rowSpacing, 'Hot Aisle - Server Exhaust', 0xd70000);
  createAisleIndicator(scene, 0, 0, 0, 'Cold Aisle - Cool Air Intake', 0x0055d7);
  createAisleIndicator(scene, 0, 0, rowSpacing, 'Hot Aisle - Server Exhaust', 0xd70000);
  
  // Create 2 rows x 16 racks
  for (let row = 0; row < 2; row++) {
    for (let col = 0; col < 16; col++) {
      // Create rack
      const rackGeometry = new THREE.BoxGeometry(rackWidth, rackHeight, rackDepth);
      
      // Different rack colors based on random rack types
      const rackColors = [
        0x666666, // Standard
        0x707070, // High Density
        0x777777, // Enterprise
      ];
      const rackColor = rackColors[Math.floor(Math.random() * rackColors.length)];
      
      const rackMaterial = new THREE.MeshStandardMaterial({
        color: rackColor,
        metalness: 0.7,
        roughness: 0.2,
      });
      const rackMesh = new THREE.Mesh(rackGeometry, rackMaterial);
      
      // Position rack in 3D space
      // Calculate X position: center racks horizontally, start from -15 (for 16 racks * spacing/2)
      const xPos = (col * rackSpacing) - ((16 * rackSpacing) / 2) + (rackSpacing / 2);
      
      // Calculate Z position: cold aisle centered at 0,
      // first row facing cold aisle (front at -rowSpacing/2)
      // second row facing cold aisle (front at rowSpacing/2)
      const zPos = row === 0 ? -rowSpacing/2 - rackDepth/2 : rowSpacing/2 + rackDepth/2;
      
      rackMesh.position.x = xPos;
      rackMesh.position.z = zPos;
      rackMesh.position.y = rackHeight / 2; // Half height to place on floor
      
      // Rotate the second row 180 degrees to face the cold aisle
      if (row === 1) {
        rackMesh.rotation.y = Math.PI;
      }
      
      scene.add(rackMesh);
      
      // Add a rack number indicator
      const rackId = row === 0 ? col + 1 : col + 17; // Matching the IDs from the 2D view
      addRackIdLabel(scene, xPos, rackHeight + 0.1, zPos, rackId.toString());
      
      // Check if we have real rack data available from the game state
      let realRackData: Rack | undefined;
      let serverCount = Math.floor(Math.random() * 6); // Default to random count for demo
      
      // Look for real rack data in the current location's floors
      if (locationData && locationData.floors) {
        for (const floorKey in locationData.floors) {
          const floor = locationData.floors[floorKey];
          if (floor.racks) {
            // Determine rack id using same pattern as in 2D view: row and column
            // Row 0, Column 0-15 are rack ids 0-15
            // Row 1, Column 0-15 are rack ids 16-31
            const rackId = (row * 16) + col;
            realRackData = floor.racks.find(r => r.id === rackId);
            if (realRackData) break;
          }
        }
      }
      
      // Use real data for server count if available
      if (realRackData?.servers) {
        serverCount = realRackData.servers.length;
      }
      
      // Calculate server height based on count
      const serverHeight = 0.15;
      const serverSpacing = 0.01;
      const totalServerStackHeight = serverCount * (serverHeight + serverSpacing);
      const startY = (rackHeight - totalServerStackHeight) / 2; // Center servers vertically in rack
      
      // Create power usage indicator for the rack if real data exists
      if (realRackData) {
        // Calculate power and cooling metrics
        const powerDraw = realRackData.powerDraw || 0;
        const maxCapacity = realRackData.maxCapacity || 100;
        const coolingNeeded = realRackData.coolingNeeded || 0;
        const effectiveCooling = realRackData.effectiveCooling || 100;
        
        const powerUsagePercent = powerDraw / maxCapacity;
        const coolingUsagePercent = coolingNeeded / effectiveCooling;
        
        // Add power indicator (vertical bar)
        const powerBarHeight = rackHeight * 0.8;
        const powerBarWidth = 0.1;
        const powerBarGeometry = new THREE.BoxGeometry(
          powerBarWidth, 
          powerBarHeight * Math.min(powerUsagePercent, 1), 
          powerBarWidth
        );
        
        // Color gradient based on usage (green->yellow->orange->red)
        let powerColor;
        if (powerUsagePercent < 0.6) powerColor = 0x4caf50; // Green
        else if (powerUsagePercent < 0.8) powerColor = 0xffeb3b; // Yellow
        else if (powerUsagePercent < 0.9) powerColor = 0xff9800; // Orange
        else powerColor = 0xf44336; // Red
        
        const powerBarMaterial = new THREE.MeshBasicMaterial({ color: powerColor });
        const powerBar = new THREE.Mesh(powerBarGeometry, powerBarMaterial);
        
        // Position the power bar to the side of the rack
        const barOffset = (row === 0) ? -0.1 : 0.1; // Adjust based on row for visibility
        powerBar.position.x = xPos - rackWidth/2 - powerBarWidth;
        powerBar.position.z = zPos + barOffset;
        powerBar.position.y = (powerBarHeight * Math.min(powerUsagePercent, 1)) / 2;
        
        scene.add(powerBar);
      }
      
      for (let i = 0; i < serverCount; i++) {
        const serverGeometry = new THREE.BoxGeometry(rackWidth * 0.8, serverHeight, rackDepth * 0.8);
        
        // Server color based on real data if available
        let serverColor = 0x555555; // Default gray
        let serverEfficiency = 0;
        
        if (realRackData && realRackData.servers && i < realRackData.servers.length) {
          const server = realRackData.servers[i];
          // Calculate server efficiency (revenue per kW) for color coding
          // Use server type to estimate revenue (since Server type may not have revenuePerDay property)
          const estimatedRevenue = server.type === 'compute' ? 5 : 
                                  server.type === 'database' ? 8 : 
                                  server.type === 'storage' ? 3 : 4; // default for web
          serverEfficiency = estimatedRevenue / (server.powerDraw || 1);
          
          // Color based on server efficiency (revenue per kW)
          if (serverEfficiency > 10) serverColor = 0x4caf50; // High efficiency - green
          else if (serverEfficiency > 5) serverColor = 0x8bc34a; // Good efficiency - light green
          else if (serverEfficiency > 2) serverColor = 0xffc107; // Medium efficiency - yellow
          else serverColor = 0xff5722; // Low efficiency - orange-red
          switch (server.type) {
            case 'web':
              serverColor = 0x2196f3; // Blue
              break;
            case 'compute':
              serverColor = 0xff5722; // Orange
              break;
            case 'storage':
              serverColor = 0x4caf50; // Green
              break;
            case 'ai':
              serverColor = 0x9c27b0; // Purple
              break;
            default:
              serverColor = 0xbdbdbd; // Gray for unknown type
          }
        } else {
          // Random color for dummy servers
          const colors = [
            0x2196f3, // Blue - web
            0xff5722, // Orange - compute
            0x4caf50, // Green - storage
            0x9c27b0, // Purple - AI
          ];
          serverColor = colors[Math.floor(Math.random() * colors.length)];
        }
        
        const serverMaterial = new THREE.MeshStandardMaterial({
          color: serverColor,
          metalness: 0.5,
          roughness: 0.1,
        });
        
        const serverMesh = new THREE.Mesh(serverGeometry, serverMaterial);
        
        // Position server within rack
        serverMesh.position.x = xPos;
        serverMesh.position.z = zPos;
        serverMesh.position.y = startY + (i * (serverHeight + serverSpacing)) + (serverHeight/2);
        
        // If the rack is in the second row, adjust the server position to match rack rotation
        if (row === 1) {
          serverMesh.rotation.y = Math.PI;
        }
        
        scene.add(serverMesh);
      }
    }
  }
  
  // Add cooling units at both ends of the cold aisle (centered at z=0)
  addCoolingUnit(scene, -18, 0, 0);
  addCoolingUnit(scene, 18, 0, 0);
  
  // Add power distribution units at corners
  addPowerUnit(scene, -18, 0, -rowSpacing-1);
  addPowerUnit(scene, 18, 0, -rowSpacing-1);
  addPowerUnit(scene, -18, 0, rowSpacing+1);
  addPowerUnit(scene, 18, 0, rowSpacing+1);
  
  // Add overall datacenter power/cooling indicators if location data exists
  if (locationData) {
    addDatacenterMetrics(scene, locationData);
  }
};

// Helper function to add a cooling unit
const addCoolingUnit = (scene: THREE.Scene, x: number, z: number, y: number) => {
  const coolingUnitGeometry = new THREE.BoxGeometry(1.2, 1.8, 1);
  const coolingUnitMaterial = new THREE.MeshStandardMaterial({
    color: 0x03a9f4, // Light blue
    metalness: 0.3,
    roughness: 0.5,
  });
  const coolingUnitMesh = new THREE.Mesh(coolingUnitGeometry, coolingUnitMaterial);
  coolingUnitMesh.position.set(x, y, z);
  scene.add(coolingUnitMesh);
  
  // Add a simple label
  const label = createTextLabel("Cooling Unit", x, y + 1.5, z);
  scene.add(label);
};

// Helper function to add a power distribution unit
const addPowerUnit = (scene: THREE.Scene, x: number, z: number, y: number) => {
  const pduGeometry = new THREE.BoxGeometry(2, 1.5, 1);
  const pduMaterial = new THREE.MeshStandardMaterial({
    color: 0xff9800, // Orange
    metalness: 0.4,
    roughness: 0.6,
  });
  const pduMesh = new THREE.Mesh(pduGeometry, pduMaterial);
  pduMesh.position.set(x, y, z);
  scene.add(pduMesh);
  
  // Add a simple label
  const label = createTextLabel("Power Unit", x, y + 1.2, z);
  scene.add(label);
};

// Create a text label using a sprite
const createTextLabel = (text: string, x: number, y: number, z: number) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return new THREE.Object3D(); // Fallback if canvas not supported
  
  canvas.width = 256;
  canvas.height = 128;
  
  // Draw text on canvas
  context.fillStyle = '#ffffff';
  context.font = '24px Arial';
  context.fillText(text, 10, 64);
  
  // Create texture from canvas
  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
  });
  
  const sprite = new THREE.Sprite(material);
  sprite.position.set(x, y, z);
  sprite.scale.set(2, 1, 1);
  
  return sprite;
};

// Create aisle indicator (hot or cold)
const createAisleIndicator = (scene: THREE.Scene, x: number, y: number, z: number, text: string, color: number) => {
  // Create a flat plane for the aisle indicator
  const planeGeometry = new THREE.PlaneGeometry(35, 1.5);
  const planeMaterial = new THREE.MeshBasicMaterial({
    color: color,
    transparent: true,
    opacity: 0.5,
    side: THREE.DoubleSide,
  });
  
  const plane = new THREE.Mesh(planeGeometry, planeMaterial);
  plane.position.set(x, y + 0.1, z);
  plane.rotation.x = -Math.PI / 2; // Lay flat
  scene.add(plane);
  
  // Add text label
  const label = createTextLabel(text, x, y + 0.5, z);
  scene.add(label);
};

// Add rack ID label
const addRackIdLabel = (scene: THREE.Scene, x: number, y: number, z: number, id: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return;
  
  canvas.width = 64;
  canvas.height = 64;
  
  // Draw text on canvas
  context.fillStyle = '#ffffff';
  context.font = 'bold 32px Arial';
  context.textAlign = 'center';
  context.fillText(id, 32, 40);
  
  // Create texture from canvas
  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
  });
  
  const sprite = new THREE.Sprite(material);
  sprite.position.set(x, y, z);
  sprite.scale.set(0.5, 0.5, 0.5);
  
  scene.add(sprite);
};

// Helper function to add datacenter-wide metrics visualization
const addDatacenterMetrics = (scene: THREE.Scene, locationData: LocationData) => {
  if (!locationData) return;
  
  // Calculate overall power utilization
  const powerUsage = locationData.usedPower || 0;
  const availablePower = locationData.availablePowerKW || 1;
  const powerUsagePercent = Math.min(powerUsage / availablePower, 1);
  
  // Calculate overall cooling utilization
  const coolingUsage = locationData.usedCooling || 0;
  const availableCooling = locationData.coolingCapacity || 1;
  const coolingUsagePercent = Math.min(coolingUsage / availableCooling, 1);
  
  // Create power gauge (left side)
  createMetricGauge(scene, -20, 5, 0, `Power: ${Math.round(powerUsage)}/${Math.round(availablePower)} kW`, powerUsagePercent);
  
  // Create cooling gauge (right side)
  createMetricGauge(scene, 20, 5, 0, `Cooling: ${Math.round(coolingUsage)}/${Math.round(availableCooling)} kW`, coolingUsagePercent);
};

// Helper function to create a metric gauge
const createMetricGauge = (scene: THREE.Scene, x: number, y: number, z: number, text: string, value: number) => {
  // Create gauge background
  const gaugeWidth = 5;
  const gaugeHeight = 0.5;
  const bgGeometry = new THREE.PlaneGeometry(gaugeWidth, gaugeHeight);
  const bgMaterial = new THREE.MeshBasicMaterial({ color: 0x333333 });
  const background = new THREE.Mesh(bgGeometry, bgMaterial);
  background.position.set(x, y, z);
  background.lookAt(0, y, 0); // Face toward center
  scene.add(background);
  
  // Create gauge fill based on value
  const fillWidth = gaugeWidth * value;
  const fillGeometry = new THREE.PlaneGeometry(fillWidth, gaugeHeight * 0.8);
  
  // Choose color based on value
  let fillColor;
  if (value < 0.6) fillColor = 0x4caf50; // Green
  else if (value < 0.8) fillColor = 0xffeb3b; // Yellow
  else if (value < 0.9) fillColor = 0xff9800; // Orange
  else fillColor = 0xf44336; // Red
  
  const fillMaterial = new THREE.MeshBasicMaterial({ color: fillColor });
  const fill = new THREE.Mesh(fillGeometry, fillMaterial);
  
  // Position fill at left edge of background with offset to be slightly in front
  fill.position.set(x - (gaugeWidth - fillWidth) / 2, y, z + 0.01);
  fill.lookAt(0, y, 0); // Face toward center
  scene.add(fill);
  
  // Add text label
  const label = createTextLabel(text, x, y + 1, z);
  scene.add(label);
};

export default Test3DView;
